import React, { useEffect, useState } from 'react';
import GmModal from 'app/shared/modal/modal';
import { Icon } from 'assets/icons';
import styles from './slideshow.module.css';

// Image carousel used by both the guides and the welcome presentation. Captions
// render only when a slide has one; `finish_label` (e.g. 'Done') turns the last
// slide's Next button into a close action — omit it to leave Next disabled on
// the last slide. State lives here so the modal can stay mounted for GmModal's
// fade animation. `closeModal` is injected by GmModal (it clones its children).
const Carousel = ({
  slides,
  index,
  framed,
  finish_label,
  onPrev,
  onNext,
  closeModal = () => {}
}) => {
  const slide = slides[index] || {};
  const is_first = index === 0;
  const is_last = index >= slides.length - 1;

  return (
    <div className={styles.carousel}>
      <div className={styles.slide}>
        {slide.image ? (
          // Guide screenshots get a soft frame; the frame's padding is why the
          // image needs its own wrapper (padding on the img won't round its bitmap).
          framed ? (
            <div className={styles.imageFrame}>
              <img src={slide.image} alt="" className={styles.image} />
            </div>
          ) : (
            <img src={slide.image} alt="" className={styles.image} />
          )
        ) : (
          <div className={styles.imagePlaceholder} />
        )}
        {slide.caption && <p className={styles.caption}>{slide.caption}</p>}
      </div>

      <div className={styles.footer}>
        <button type="button" className={styles.navButton} disabled={is_first} onClick={onPrev}>
          <Icon name="arrow_right" width={14} height={14} className={styles.prevIcon} />
          <span>Prev</span>
        </button>

        <span className={styles.counter}>
          {index + 1} / {slides.length}
        </span>

        {is_last && finish_label ? (
          <button type="button" className={styles.navButton} onClick={closeModal}>
            <span>{finish_label}</span>
          </button>
        ) : (
          <button type="button" className={styles.navButton} disabled={is_last} onClick={onNext}>
            <span>Next</span>
            <Icon name="arrow_right" width={14} height={14} />
          </button>
        )}
      </div>
    </div>
  );
};

export const SlideshowModal = ({
  slides = [],
  show,
  onClose,
  title,
  finish_label,
  framed = true,
  // Resetting to the first slide keys off this alongside `show`, so reopening
  // a different set of slides starts from the beginning.
  reset_key
}) => {
  const [index, setIndex] = useState(0);

  useEffect(() => {
    if (show) setIndex(0);
  }, [show, reset_key]);

  return (
    <GmModal
      show_modal={show}
      show_title={!!title}
      title={title}
      onClose={onClose}
      bodyClassName={styles.body}
    >
      <Carousel
        slides={slides}
        index={index}
        framed={framed}
        finish_label={finish_label}
        onPrev={() => setIndex((current) => Math.max(0, current - 1))}
        onNext={() => setIndex((current) => Math.min(slides.length - 1, current + 1))}
      />
    </GmModal>
  );
};
